<template>
  <div class="error-page">
    <page-meta />

    <div class="container">
      <div class="error-inner">
        <figure class="error-art">
          <img :src="art" :alt="t(copy.title)" />
        </figure>

        <section-title
          :level="1"
          :kicker="code"
          :title="t(copy.title)"
          :lede="t(copy.lede)"
          flush
        >
          <div class="action-row">
            <app-button :to="route('index')" icon="house">
              {{ t('Back to the home page') }}
            </app-button>
            <app-button :to="route('web-design')" variant="outline" icon="arrow-right">
              {{ t('See our services') }}
            </app-button>
          </div>
        </section-title>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The error page deliberately does not use `Layouts/Public.vue`. A 404 or a
   500 is not a page of the site: the header, footer, chat and rails are all
   navigation into a site the visitor has just failed to reach, so the page is
   one centred panel with the way back on it. */
.error-page {
  align-items: center;
  background: $ground-tint;
  display: flex;
  min-height: 100vh;
  /* The mobile browser chrome collapses as the page scrolls, so 100vh is taller
     than the visible area and the centred panel sits under the address bar.
     dvh tracks the visible viewport; the vh above is the fallback. */
  min-height: 100dvh;
  padding: 4rem 0;

  @media (max-width: 575.98px) {
    padding: 3rem 0;
  }
}

.error-inner {
  margin: 0 auto;
  max-width: 46rem;
  text-align: center;

  .action-row {
    justify-content: center;
  }
}

.error-art {
  margin: 0 auto 2rem;
  max-width: 22rem;

  img {
    display: block;
    height: auto;
    width: 100%;
  }

  @media (max-width: 575.98px) {
    margin-bottom: 1.25rem;
    max-width: 15rem;
  }
}
</style>

<script>
import { faArrowRight, faHouse } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import PageMeta from '@/Components/PageMeta.vue';
import SectionTitle from '@/Components/SectionTitle.vue';

FontAwesomeLibrary.add(faArrowRight, faHouse);

/* The page every non-local error lands on.

   `Handler.php` renders `Error` for 400, 401, 403, 404, 429, 500 and 503 once
   the app is not local, and until now no such page existed, so every
   production 404 or 500 resolved to nothing. The seven illustrations in
   `public/images/errors/` were waiting for this page. */
export default {
  components: {
    AppButton,
    PageMeta,
    SectionTitle
  },

  props: {
    /* The HTTP status the handler rescued. Anything unrecognised falls back to
       the 500 copy and illustration rather than rendering an empty page. */
    status: {
      type: [Number, String],
      default: 500
    }
  },

  computed: {
    code() {
      return String(this.status);
    },

    copy() {
      return this.copies[this.code] || this.copies[500];
    },

    /* The art is keyed by status, so an unrecognised code borrows the 500
       illustration rather than requesting a file that does not exist. */
    art() {
      return '/images/errors/' + (this.copies[this.code] ? this.code : 500) + '.svg';
    },

    copies() {
      return {
        400: {
          title: this.t('Bad request'),
          lede: this.t('This request could not be read. Go back, or start again from the home page.')
        },
        401: {
          title: this.t('Sign in required'),
          lede: this.t('This page needs a sign-in first. If you followed a link to get here, ask us for a fresh one.')
        },
        403: {
          title: this.t('Not allowed'),
          lede: this.t('You do not have access to this page. If that is wrong, get in touch and we will sort it out.')
        },
        404: {
          title: this.t('Page not found'),
          lede: this.t('The page you were looking for has moved or never existed. Try the home page, or tell us what you were looking for.')
        },
        429: {
          title: this.t('Too many requests'),
          lede: this.t('You have made too many requests in a short time. Wait a minute and try again.')
        },
        500: {
          title: this.t('Something went wrong'),
          lede: this.t('Our side hit an error while building this page. That is on us, not on you. Please try again in a moment.')
        },
        503: {
          title: this.t('Service unavailable'),
          lede: this.t('The site is briefly offline for maintenance. It will be back shortly.')
        }
      };
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Bad request': 'Bad request',
          'This request could not be read. Go back, or start again from the home page.': 'This request could not be read. Go back, or start again from the home page.',
          'Sign in required': 'Sign in required',
          'This page needs a sign-in first. If you followed a link to get here, ask us for a fresh one.': 'This page needs a sign-in first. If you followed a link to get here, ask us for a fresh one.',
          'Not allowed': 'Not allowed',
          'You do not have access to this page. If that is wrong, get in touch and we will sort it out.': 'You do not have access to this page. If that is wrong, get in touch and we will sort it out.',
          'Page not found': 'Page not found',
          'The page you were looking for has moved or never existed. Try the home page, or tell us what you were looking for.': 'The page you were looking for has moved or never existed. Try the home page, or tell us what you were looking for.',
          'Too many requests': 'Too many requests',
          'You have made too many requests in a short time. Wait a minute and try again.': 'You have made too many requests in a short time. Wait a minute and try again.',
          'Something went wrong': 'Something went wrong',
          'Our side hit an error while building this page. That is on us, not on you. Please try again in a moment.': 'Our side hit an error while building this page. That is on us, not on you. Please try again in a moment.',
          'Service unavailable': 'Service unavailable',
          'The site is briefly offline for maintenance. It will be back shortly.': 'The site is briefly offline for maintenance. It will be back shortly.',
          'Back to the home page': 'Back to the home page',
          'See our services': 'See our services'
        },
        'zh-MY': {
          'Bad request': '无效请求',
          'This request could not be read. Go back, or start again from the home page.': '无法读取这项请求。请返回上一页，或从首页重新开始。',
          'Sign in required': '需要登录',
          'This page needs a sign-in first. If you followed a link to get here, ask us for a fresh one.': '这个页面需要先登录。如果您是通过链接来到这里，请向我们索取新的链接。',
          'Not allowed': '无权访问',
          'You do not have access to this page. If that is wrong, get in touch and we will sort it out.': '您没有访问这个页面的权限。如果这并不正确，请联系我们，我们会尽快处理。',
          'Page not found': '找不到页面',
          'The page you were looking for has moved or never existed. Try the home page, or tell us what you were looking for.': '您要找的页面已经搬移，或从未存在。可以回到首页，或告诉我们您在找什么。',
          'Too many requests': '请求过于频繁',
          'You have made too many requests in a short time. Wait a minute and try again.': '您在短时间内发出了太多请求。请等一分钟后再试。',
          'Something went wrong': '出了点问题',
          'Our side hit an error while building this page. That is on us, not on you. Please try again in a moment.': '我们在生成这个页面时遇到错误。这是我们的问题，不是您的。请稍后再试一次。',
          'Service unavailable': '服务暂时不可用',
          'The site is briefly offline for maintenance. It will be back shortly.': '本站正在短暂维护，很快就会恢复。',
          'Back to the home page': '回到首页',
          'See our services': '看看我们的服务'
        },
        'ms-MY': {
          'Bad request': 'Permintaan tidak sah',
          'This request could not be read. Go back, or start again from the home page.': 'Permintaan ini tidak dapat dibaca. Kembali, atau mula semula dari laman utama.',
          'Sign in required': 'Perlu log masuk',
          'This page needs a sign-in first. If you followed a link to get here, ask us for a fresh one.': 'Halaman ini memerlukan log masuk dahulu. Jika anda sampai ke sini melalui pautan, minta pautan baharu daripada kami.',
          'Not allowed': 'Tidak dibenarkan',
          'You do not have access to this page. If that is wrong, get in touch and we will sort it out.': 'Anda tiada akses ke halaman ini. Jika ini tidak betul, hubungi kami dan kami akan betulkan.',
          'Page not found': 'Halaman tidak dijumpai',
          'The page you were looking for has moved or never existed. Try the home page, or tell us what you were looking for.': 'Halaman yang anda cari telah berpindah atau tidak pernah wujud. Cuba laman utama, atau beritahu kami apa yang anda cari.',
          'Too many requests': 'Terlalu banyak permintaan',
          'You have made too many requests in a short time. Wait a minute and try again.': 'Anda membuat terlalu banyak permintaan dalam masa singkat. Tunggu sebentar dan cuba lagi.',
          'Something went wrong': 'Ada sesuatu yang tidak kena',
          'Our side hit an error while building this page. That is on us, not on you. Please try again in a moment.': 'Pihak kami menghadapi ralat semasa membina halaman ini. Ini kesalahan kami, bukan anda. Sila cuba lagi sebentar lagi.',
          'Service unavailable': 'Perkhidmatan tidak tersedia',
          'The site is briefly offline for maintenance. It will be back shortly.': 'Laman ini ditutup seketika untuk penyelenggaraan. Ia akan kembali tidak lama lagi.',
          'Back to the home page': 'Kembali ke laman utama',
          'See our services': 'Lihat perkhidmatan kami'
        }
      }
    });

    return { t };
  }
};
</script>
